import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { useRootStore } from "../../../../store/contexts";
import { ShowReference } from "../../../common/controls";
import { ShowMultiLine } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { EvaluationRating } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type AiSearchResponseViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  conversationValue: { displayId: string; id: string } | null;
  queryValue?: { displayId: string; id: string } | null;
  query: string;
  responseValue?: { displayId: string; id: string } | null;
  response: string;
  sourcesValue?: { displayId: string; id: string } | null;
  sources: string;
  explanationValue?: { displayId: string; id: string } | null;
  explanation: string;
  tokenUsageValue?: { displayId: string; id: string } | null;
  tokenUsage: string;
  createdAtValue?: { displayId: string; id: string } | null;
  createdAt: Date | null;
  ratingOptions: { id: string; displayId: string }[];
  rating: EvaluationRating | "";
  evaluatedAtValue?: { displayId: string; id: string } | null;
  evaluatedAt: Date | null;
  evaluatedByValue: { displayId: string; id: string } | null;
  toolCallsRows: CrudRows;
  toolCallsVisibleColumns: GridColumn[];
  toolCallsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
};

export const AiSearchResponseViewForm: React.FC<AiSearchResponseViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      conversationValue,
      query = "",
      response = "",
      sources = "{}",
      explanation = "{}",
      tokenUsage = "{}",
      createdAt = null,
      ratingOptions = [],
      rating = "" as EvaluationRating | "",
      evaluatedAt = null,
      evaluatedByValue,
      toolCallsRows,
      toolCallsVisibleColumns,
      toolCallsSetColumnWiths,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupAiSearchResponseLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowReference
                  label={langStore.AiSearchResponseConversationLabel}
                  selectedValue={langStore.displayIdByLang(
                    conversationValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={query}
                  label={langStore.AiSearchResponseQueryLabel}
                />
                <ShowMultiLine
                  value={response}
                  label={langStore.AiSearchResponseResponseLabel}
                />
                <InputCodeEditor
                  value={sources}
                  onChange={() => {}}
                  label={langStore.AiSearchResponseSourcesLabel}
                  language="json"
                  readOnly={true}
                />
                <InputCodeEditor
                  value={explanation}
                  onChange={() => {}}
                  label={langStore.AiSearchResponseExplanationLabel}
                  language="json"
                  readOnly={true}
                />
                <InputCodeEditor
                  value={tokenUsage}
                  onChange={() => {}}
                  label={langStore.AiSearchResponseTokenUsageLabel}
                  language="json"
                  readOnly={true}
                />
                <ShowDateTime
                  value={createdAt}
                  label={langStore.AiSearchResponseCreatedAtLabel}
                />
                <ShowString
                  value={
                    rating
                      ? ratingOptions.find(
                          (o: { id: string }) => o.id === rating
                        )?.displayId || rating
                      : ""
                  }
                  label={langStore.AiSearchResponseRatingLabel}
                />
                <ShowDateTime
                  value={evaluatedAt}
                  label={langStore.AiSearchResponseEvaluatedAtLabel}
                />
                <ShowReference
                  label={langStore.AiSearchResponseEvaluatedByLabel}
                  selectedValue={langStore.displayIdByLang(
                    evaluatedByValue?.displayId
                  )}
                />
                <ShowItemsCompact
                  rows={toolCallsRows}
                  showLabel={true}
                  label={langStore.AiSearchResponseToolCallsLabel}
                  columns={toolCallsVisibleColumns}
                  setColumnWidths={toolCallsSetColumnWiths}
                  entityName="AiSearchToolCall"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("aiSearchToolCall", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
